'use client'

import { useCallback, useRef } from 'react'
import { useTranslation } from '@/contexts/I18nContext'
import { useToast } from '@/hooks/useToast'

function getReadAllToast(updated: number) {
  if (updated > 0) {
    return {
      descriptionKey: 'Message.Notification.AllReadSuccess' as const,
      variant: 'success' as const,
    }
  }
  return {
    descriptionKey: 'Message.Notification.NoUnread' as const,
    variant: 'default' as const,
  }
}

export function useNotificationReadAll(readAll: () => Promise<number>, listLoading: boolean) {
  const { t } = useTranslation('message')
  const { toast } = useToast()
  const inFlightRef = useRef(false)

  const handleReadAll = useCallback(async () => {
    if (inFlightRef.current || listLoading) return

    inFlightRef.current = true
    try {
      const updated = await readAll()
      const result = getReadAllToast(updated)
      toast({
        description: t(result.descriptionKey),
        variant: result.variant,
      })
    } catch (error) {
      console.error('Failed to mark notifications as read:', error)
      toast({ description: t('Message.Notification.ReadAllFailed'), variant: 'error' })
    } finally {
      inFlightRef.current = false
    }
  }, [listLoading, readAll, t, toast])

  return { handleReadAll, disabled: listLoading }
}
